import { LitElement, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { CheckOptions } from '@vitesse/statechart-host';
import { type CodeLanguage } from './code-lang';

type IEditor = import('monaco-editor/esm/vs/editor/editor.api').editor.IStandaloneCodeEditor;
type MonacoApi = typeof import('monaco-editor/esm/vs/editor/editor.api');

/*
 * <vitesse-code-view> — the ONE reusable read-only code viewer (Monaco).
 *
 * Monaco is LAZY-LOADED (the whole editor lands in its own chunk on first mount
 * via the dynamic import of ./monaco-shell), so the main bundle carries zero
 * Monaco bytes. Structured Text + SCXML/XML get syntax highlighting, keyword
 * hover docs, folding, and — opt-in via `minimap` — the real VS Code minimap.
 * Read-only. Light DOM (the app's SCSS themes the `.code-view` frame).
 */
@customElement('vitesse-code-view')
export class VitesseCodeView extends LitElement {
  @property() value = '';
  @property() language: CodeLanguage = 'xml';
  /** Legacy CM sizing hints — Monaco fills its (bounded) container. Kept for API
   *  compatibility; no behavioural effect today. */
  @property({ type: Boolean }) grow = false;
  @property() maxHeight = '';
  @property() fontFamily = '';
  /** Show Monaco's minimap — the program view opts in; snippet views don't. */
  @property({ type: Boolean }) minimap = false;
  /**
   * LET THE READER TYPE.
   *
   * Off by default, because every surface that had this until now is looking at something it does
   * not own: a PLC's program, a run's archived statechart. A widget's source is different — it is a
   * draft somebody is reviewing before it goes on a board, and the review that cannot fix the typo
   * it just found is half a review.
   */
  @property({ type: Boolean }) editable = false;
  /** Every keystroke, debounced by Monaco's own change batching. The host owns the text. */
  @property({ attribute: false }) onEdit?: (value: string) => void;
  /**
   * What the language service thinks, whenever it changes its mind.
   *
   * Reported rather than drawn: the editor already underlines the line, and the surface around it is
   * where a count belongs — a person who scrolled away from the error still needs to know it is
   * there. Markers arrive asynchronously (the service runs in a worker), so this fires again after
   * the text has settled, which is exactly when the answer is worth having.
   */
  @property({ attribute: false }) onProblems?: (problems: readonly { line: number; message: string }[]) => void;
  /**
   * A STATECHART'S INTERFACE AND CHILDREN, for an SCXML view of a model. With them the view runs the
   * check the assistant's tools run, underlines what it finds, and answers a hover on a state or a
   * variable from the language service (scxml-service.ts). Without them it is the plain viewer it
   * always was, as for a run's archived chart. Squiggles are drawn even though the view is read-only:
   * the person can ask the assistant to fix what they see.
   */
  @property({ attribute: false }) scxmlCheck?: CheckOptions;

  private editor?: IEditor;
  private monaco?: MonacoApi;
  /** Undoes the current check's markers and hover; replaced each time the text or the options change. */
  private serviceOff?: () => void;
  /** Which check is current, so one that finishes after a newer one started is dropped. */
  private serviceToken = 0;
  private disposed = false;

  protected createRenderRoot(): HTMLElement {
    return this;
  }
  render() {
    return html`<div class="code-view"></div>`;
  }

  protected async firstUpdated(): Promise<void> {
    try {
      await this.mount();
    } catch {
      // The viewer must NEVER take the page down — fall back to plain text.
      const host = this.querySelector('.code-view');
      if (host) host.textContent = this.value;
    }
  }

  private async mount(): Promise<void> {
    const shell = await import('./monaco-shell');
    if (this.disposed) return;
    const monaco = shell.ensureMonaco();
    this.monaco = monaco;
    const host = this.querySelector('.code-view') as HTMLElement;
    // Every id here is one Monaco has registered (see monaco-shell's contributions); an id nothing
    // registered falls back to plaintext SILENTLY, which is a grey wall with no error to explain it.
    const langId = this.language === 'none' ? 'plaintext' : this.language;
    this.editor = monaco.editor.create(host, {
      value: this.value,
      language: langId,
      theme: shell.currentMonacoTheme(),
      readOnly: !this.editable,
      domReadOnly: !this.editable,
      minimap: { enabled: this.minimap, renderCharacters: true, maxColumn: 60, showSlider: 'mouseover', scale: 3 },
      lineNumbers: 'on',
      lineNumbersMinChars: 3,
      folding: true,
      foldingHighlight: false,
      showFoldingControls: 'mouseover', // chevrons on gutter-hover (Monaco default)
      scrollBeyondLastLine: false,
      automaticLayout: true, // relayout when the (fitHeight-bounded) container resizes
      fontFamily: this.fontFamily || "'JetBrains Mono', 'SF Mono', ui-monospace, monospace",
      fontSize: 13,
      lineHeight: 20,
      wordWrap: 'off',
      renderLineHighlight: 'none',
      renderWhitespace: 'none',
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6, useShadows: false },
      guides: { indentation: false },
      contextmenu: false,
      matchBrackets: 'never',
      occurrencesHighlight: 'off',
      selectionHighlight: false,
      links: false,
      cursorBlinking: 'solid',
      padding: { top: 6, bottom: 6 },
      // What the language service knows, in the colours the tokenizer's own tokens already use — see
      // the note on `semanticHighlighting` in base-ui's monaco-theme.
      'semanticHighlighting.enabled': true,
      /*
       * AND NO RAINBOW BRACKETS.
       *
       * Monaco colours nested bracket pairs by depth out of the box — orchid, gold, a second blue —
       * which is three hues the palette deliberately does not have, applied to the one thing in a
       * program that carries no meaning of its own. Beside ST and SCXML, which have no such thing,
       * a widget's source read as a different product.
       */
      bracketPairColorization: { enabled: false },
      // Only where the reader can act on them: a squiggle under text nobody may change is noise.
      quickSuggestions: this.editable,
      suggestOnTriggerCharacters: this.editable,
      parameterHints: { enabled: this.editable },
      hover: { enabled: true },
      /*
       * HOVERS AND SUGGESTIONS ARE NOT CLIPPED BY THE FRAME.
       *
       * Monaco measures the room above a line against the whole page, not against the editor. On the
       * first lines it sees the page header above, puts the hover there, and every frame around the
       * view (overflow: hidden, so the editor inner-scrolls) cuts it off. Fixed widgets are placed in
       * the viewport, outside those frames. No host of this view moves it with a CSS transform,
       * which would make the fixed position relative to that host instead.
       */
      fixedOverflowWidgets: true,
      // Monaco draws validation squiggles only in an editable editor unless told otherwise.
      renderValidationDecorations: this.scxmlCheck ? 'on' : 'editable',
    });
    if (this.scxmlCheck && langId === 'xml') void this.checkScxml();
    if (!this.editable) return;
    this.editor.onDidChangeModelContent(() => {
      const next = this.editor?.getValue() ?? '';
      if (next === this.value) return;
      // Held so `updated` does not push the same text back in and reset the caret mid-word.
      this.value = next;
      this.onEdit?.(next);
    });
    /*
     * THE SERVICE ANSWERS LATE, and that is the whole reason this is a subscription.
     *
     * Diagnostics come from a worker: on the keystroke that breaks the file there are no markers
     * yet, and a check run inline would report the previous state of the world. Monaco says when it
     * has an answer; this passes it on.
     */
    const model = this.editor.getModel();
    if (!model) return;
    const push = (): void => {
      const markers = monaco.editor.getModelMarkers({ resource: model.uri });
      // Errors only. A widget that warns is a widget; a widget that does not parse is not one.
      this.onProblems?.(markers.filter((mk) => mk.severity >= 8).map((mk) => ({ line: mk.startLineNumber, message: mk.message })));
    };
    const sub = monaco.editor.onDidChangeMarkers((uris) => {
      if (uris.some((u) => u.toString() === model.uri.toString())) push();
    });
    this.markerOff = () => sub.dispose();
    push();
  }

  /** The Monaco namespace this editor was built with — see the marker subscription above. */
  private markerOff?: () => void;

  protected updated(changed: PropertyValues): void {
    if (changed.has('value') && this.editor && this.editor.getValue() !== this.value) {
      this.editor.setValue(this.value);
    }
    if (this.editor && (changed.has('value') || changed.has('scxmlCheck')) && this.language === 'xml') {
      this.editor.updateOptions({ renderValidationDecorations: this.scxmlCheck ? 'on' : 'editable' });
      void this.checkScxml();
    }
  }

  /** Replace the SCXML check's markers and hover with ones for the text as it is now; clear them when there are no options. */
  private async checkScxml(): Promise<void> {
    const token = ++this.serviceToken;
    this.serviceOff?.();
    this.serviceOff = undefined;
    const model = this.editor?.getModel();
    if (!this.scxmlCheck || !model || !this.monaco) return;
    try {
      const { attachScxmlService } = await import('./scxml-service');
      const off = await attachScxmlService(this.monaco, model, this.scxmlCheck);
      if (token === this.serviceToken && !this.disposed) this.serviceOff = off;
      else off();
    } catch {
      // The view stays a view without them: a check that could not load must not take the code away.
    }
  }

  /** Fold every foldable block (the toolbar collapse-all). */
  foldAll(): void {
    void this.editor?.getAction('editor.foldAll')?.run();
  }
  /** Unfold everything (the toolbar expand-all). */
  unfoldAll(): void {
    void this.editor?.getAction('editor.unfoldAll')?.run();
  }
  /** Scroll a 1-based line near the top of the viewport (index → block jump). */
  scrollToLine(line: number): void {
    if (!this.editor) return;
    const n = Math.max(1, Math.min(line, this.editor.getModel()?.getLineCount() ?? line));
    this.editor.revealLineNearTop(n);
    this.editor.setPosition({ lineNumber: n, column: 1 });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.disposed = true;
    this.serviceToken++;
    this.serviceOff?.();
    this.serviceOff = undefined;
    this.markerOff?.();
    this.markerOff = undefined;
    this.editor?.dispose();
    this.editor = undefined;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-code-view': VitesseCodeView;
  }
}
